import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/student-characters-1739.jpg';

function YouthStudentEmployment() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (match extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Program Tracks',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Summer youth employment:', 'Paid short-term placements with supervision and skills training.'],
            ['Year-round internships:', 'Part-time roles aligned to school schedules and credit options.'],
            ['Apprenticeship prep:', 'Pre-apprenticeship and entry pathways into registered programs.'],
          ].map(([k, v], i) => (
            <li key={`tracks-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'First Jobs & Internships',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Resume basics:', 'One-page resume, relevant courses, activities, volunteer work.'],
            ['Applications:', 'Simple master application to reuse; track submissions and follow-ups.'],
            ['Interviews:', 'Practice common questions; bring a copy of your resume and references.'],
          ].map(([k, v], i) => (
            <li key={`first-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Work Permits, Hours & Eligibility',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Age requirements:', 'Some roles require applicants to be 16+; limited roles available at 14–15 where permitted.'],
            ['Work permits:', 'If required in your area, obtain a minor work permit through your school or local authority.'],
            ['School in session:', 'Youth shift limits may apply on school days; verify hour caps before scheduling.'],
          ].map(([k, v], i) => (
            <li key={`permit-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Career Readiness Skills',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Workplace habits:', 'On-time arrival, communication, problem-solving, and teamwork.'],
            ['Digital basics:', 'Email etiquette, calendars, shared docs, and safe online conduct.'],
            ['Financial literacy:', 'Direct deposit, paystubs, taxes, and budgeting your first checks.'],
          ].map(([k, v], i) => (
            <li key={`readiness-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Support & Accessibility',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Coaches & mentors:', 'Placement coaches help with onboarding, feedback, and resolving issues.'],
            ['Transportation & gear:', 'Bus passes or basic equipment may be provided for eligible participants.'],
            ['Accommodations:', 'Request reasonable accommodations for disabilities or language access needs.'],
          ].map(([k, v], i) => (
            <li key={`support-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Employer Partnerships',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Host sites:', 'Local employers offer supervised roles and real-world projects.'],
            ['Evaluation & feedback:', 'Structured check-ins to build skills and address performance early.'],
            ['Hiring pipelines:', 'Interns can convert to part-time or full-time roles after graduation.'],
          ].map(([k, v], i) => (
            <li key={`employers-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'After You’re Hired',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Onboarding:', 'I-9 docs, W-4, direct deposit, and workplace policies.'],
            ['Performance goals:', 'Agree on clear goals for the first 30–60–90 days.'],
            ['References & portfolios:', 'Save work samples and request references as you complete projects.'],
          ].map(([k, v], i) => (
            <li key={`hired-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Rights, Safety & Conduct',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Safe work:', 'Youth are restricted from hazardous tasks; report unsafe conditions.'],
            ['Fair treatment:', 'No harassment or discrimination; know how to report issues.'],
            ['Breaks & pay:', 'Follow applicable break rules; keep records of hours and pay.'],
          ].map(([k, v], i) => (
            <li key={`rights-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Youth and Student Employment Initiatives" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Youth and Student Employment Initiatives</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                Early work experience, paid internships, and career coaching—so students build real skills, earn income,
                and move into in-demand jobs with clear next steps after graduation.
              </p>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We connect youth to quality work, remove barriers to access, and coordinate with schools and employers to
                deliver real pathways from classroom to career.
              </p>
            </div>

            <div>
              <Link to="/department/employment">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default YouthStudentEmployment;
